import { useState } from 'react'
import Chevron from 'assets/icons/Chevron'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Flex, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { IOpsMachineDay, IOpsWorkerDay } from 'types/opsAnalytics.interface'
import {
  IOpsHistoryMachineRow,
  IOpsHistoryWorkerRow,
  IOpsRangePreset,
  IOpsTimelineHistoryProps,
} from 'types/opsTimelines.interface'
import { IStatusPill } from 'types/ui/statusPill.interface'
import { DayStrip } from './DayStrip'
import { DayBlock, ToneValue } from './Timelines.styled'

const RANGE_PRESETS: IOpsRangePreset[] = [
  { key: 'last7', label: 'Last 7 days', count: 7 },
  { key: 'last30', label: 'Last 30 days', count: 30 },
  { key: 'all', label: 'All', count: null },
]

const STRIP_STACK_LIMIT = 7

const fmtInt = (value: number): string => value.toLocaleString('en-US')

const utilTone = (pct: number): IStatusPill['status'] =>
  pct >= 80 ? 'success' : pct >= 65 ? 'warning' : 'danger'

const gradeTone = (grade: number): IStatusPill['status'] =>
  grade >= 8 ? 'success' : grade >= 5 ? 'neutral' : 'danger'

const gradeLabel = (grade: number | null): string => (grade == null ? 'Not graded' : `${grade} / 10`)

const machineMini = (day: IOpsMachineDay): string =>
  `Util ${day.utilizationPct}% · Avail ${day.availabilityPct}% · ${day.loads} loads · ${fmtInt(day.tonnes)} t · ${day.breakdownCount} breakdowns · GHS ${fmtInt(day.costOfDay)}`

const workerMini = (day: IOpsWorkerDay): string =>
  `Active ${day.activeHours} h · ${day.tasksDone} tasks · ${day.loads} loads · Grade ${gradeLabel(day.grade)} · ${day.rewardPoints} pts`

const machineRollup = (days: IOpsMachineDay[], label: string): string => {
  const loads = days.reduce((total, d) => total + d.loads, 0)
  const tonnes = days.reduce((total, d) => total + d.tonnes, 0)
  const breakdowns = days.reduce((total, d) => total + d.breakdownCount, 0)
  const cost = days.reduce((total, d) => total + d.costOfDay, 0)
  const avgUtil = days.length
    ? Math.round(days.reduce((total, d) => total + d.utilizationPct, 0) / days.length)
    : 0
  return `${label}: ${fmtInt(loads)} loads · ${fmtInt(tonnes)} t · avg util ${avgUtil}% · ${breakdowns} breakdowns · GH₵ ${fmtInt(Math.round(cost / 1000))}k`
}

const workerRollup = (days: IOpsWorkerDay[], label: string): string => {
  const loads = days.reduce((total, d) => total + d.loads, 0)
  const graded = days.filter((d) => d.grade != null)
  const avgGrade = graded.length
    ? (graded.reduce((total, d) => total + (d.grade ?? 0), 0) / graded.length).toFixed(1)
    : null
  const activeH = Math.round(days.reduce((total, d) => total + d.activeHours, 0))
  return `${label}: ${fmtInt(loads)} loads · ${avgGrade != null ? `avg grade ${avgGrade} (graded days only)` : 'no graded days'} · ${fmtInt(activeH)} active h`
}

const machineColumns = (expandedId: string | null): IHonestTableColumn<IOpsHistoryMachineRow>[] => [
  {
    id: 'date',
    header: 'Date',
    sticky: true,
    minWidth: '9rem',
    cell: (item) => (
      <Flex center gap='0.5rem'>
        <Chevron open={expandedId === item.id} />
        <BodyText weight='600'>{item.day.dateLabel}</BodyText>
      </Flex>
    ),
  },
  {
    id: 'util',
    header: 'Util %',
    align: 'right',
    cell: (item) => (
      <ToneValue $tone={utilTone(item.day.utilizationPct)}>{`${item.day.utilizationPct}%`}</ToneValue>
    ),
  },
  {
    id: 'loads',
    header: 'Loads',
    align: 'right',
    cell: (item) => <BodyText>{item.day.loads}</BodyText>,
  },
  {
    id: 'tonnes',
    header: 'Tonnes',
    align: 'right',
    cell: (item) => <BodyText>{fmtInt(item.day.tonnes)}</BodyText>,
  },
  {
    id: 'breakdowns',
    header: 'Breakdowns',
    align: 'right',
    cell: (item) => (
      <ToneValue $tone={item.day.breakdownCount > 0 ? 'danger' : undefined}>
        {item.day.breakdownCount}
      </ToneValue>
    ),
  },
  {
    id: 'cost',
    header: 'Cost',
    align: 'right',
    cell: (item) => <BodyText>{`GHS ${fmtInt(item.day.costOfDay)}`}</BodyText>,
  },
]

const workerColumns = (expandedId: string | null): IHonestTableColumn<IOpsHistoryWorkerRow>[] => [
  {
    id: 'date',
    header: 'Date',
    sticky: true,
    minWidth: '9rem',
    cell: (item) => (
      <Flex center gap='0.5rem'>
        <Chevron open={expandedId === item.id} />
        <BodyText weight='600'>{item.day.dateLabel}</BodyText>
      </Flex>
    ),
  },
  {
    id: 'active',
    header: 'Active h',
    align: 'right',
    cell: (item) => <BodyText>{`${item.day.activeHours} h`}</BodyText>,
  },
  {
    id: 'loads',
    header: 'Loads',
    align: 'right',
    cell: (item) => <BodyText>{item.day.loads}</BodyText>,
  },
  {
    id: 'grade',
    header: 'Grade',
    align: 'center',
    cell: (item) =>
      item.day.grade == null ? (
        <StatusPill status='neutral'>Not graded</StatusPill>
      ) : (
        <StatusPill status={gradeTone(item.day.grade)}>{item.day.grade}</StatusPill>
      ),
  },
  {
    id: 'issues',
    header: 'Issues',
    align: 'right',
    cell: (item) => <BodyText>{item.day.issuesReported}</BodyText>,
  },
  {
    id: 'points',
    header: 'Points',
    align: 'right',
    cell: (item) => <BodyText>{item.day.rewardPoints}</BodyText>,
  },
]

export const TimelineHistory = ({
  variant,
  machineDays,
  workerDays,
  startHour,
  windowHours,
}: IOpsTimelineHistoryProps) => {
  const [rangeKey, setRangeKey] = useState('last7')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const preset = RANGE_PRESETS.find((p) => p.key === rangeKey) ?? RANGE_PRESETS[0]
  const sliceRange = <T,>(days: T[]): T[] => (preset.count == null ? days : days.slice(0, preset.count))
  const mDays = variant === 'machine' ? sliceRange(machineDays ?? []) : []
  const wDays = variant === 'worker' ? sliceRange(workerDays ?? []) : []
  const rollupLabel =
    preset.count == null ? `All ${(variant === 'machine' ? mDays : wDays).length} days` : preset.label
  const machineRows: IOpsHistoryMachineRow[] = mDays.map((d) => ({ id: d.dateLabel, day: d }))
  const workerRows: IOpsHistoryWorkerRow[] = wDays.map((d) => ({ id: d.dateLabel, day: d }))
  const expandedMachine = machineRows.find((r) => r.id === expandedId) ?? null
  const expandedWorker = workerRows.find((r) => r.id === expandedId) ?? null
  const selectRange = (key: string) => {
    setRangeKey(key)
    setExpandedId(null)
  }
  const toggleRow = (rowId: string) => setExpandedId((prev) => (prev === rowId ? null : rowId))

  return (
    <Container>
      <ContainerHeader>
        <TitleText>History</TitleText>
      </ContainerHeader>
      <ContainerContent gap='1rem'>
        <TabsContainer>
          {RANGE_PRESETS.map((p) => (
            <Tabs key={p.key} active={rangeKey === p.key} onClick={() => selectRange(p.key)}>
              {p.label}
            </Tabs>
          ))}
        </TabsContainer>
        {variant === 'machine' ? (
          <>
            {mDays
              .slice(0, STRIP_STACK_LIMIT)
              .filter((d) => d.segments.length > 0)
              .map((d) => (
                <DayBlock key={d.dateLabel}>
                  <DayStrip
                    segments={d.segments}
                    dateLabel={d.dateLabel}
                    startHour={startHour}
                    windowHours={windowHours}
                  />
                  <OtherText grey>{machineMini(d)}</OtherText>
                </DayBlock>
              ))}
            <BodyText weight='600'>{machineRollup(mDays, rollupLabel)}</BodyText>
            <HonestTable
              columns={machineColumns(expandedId)}
              data={machineRows}
              keyField='id'
              onClick={(item) => toggleRow(item.id)}
            />
            {expandedMachine && (
              <DayBlock>
                <OtherText weight='600'>{expandedMachine.day.dateLabel}</OtherText>
                {expandedMachine.day.segments.length ? (
                  <>
                    <DayStrip
                      segments={expandedMachine.day.segments}
                      dateLabel={expandedMachine.day.dateLabel}
                      startHour={startHour}
                      windowHours={windowHours}
                    />
                    <OtherText grey>{machineMini(expandedMachine.day)}</OtherText>
                  </>
                ) : (
                  <OtherText grey>Strip not stored for this day — totals only.</OtherText>
                )}
              </DayBlock>
            )}
          </>
        ) : (
          <>
            {wDays
              .slice(0, STRIP_STACK_LIMIT)
              .filter((d) => d.segments.length > 0)
              .map((d) => (
                <DayBlock key={d.dateLabel}>
                  <DayStrip
                    segments={d.segments}
                    dateLabel={`${d.dateLabel} · ${d.loginAt} → ${d.logoutAt}`}
                    variant='worker'
                    startHour={startHour}
                    windowHours={windowHours}
                  />
                  <OtherText grey>{workerMini(d)}</OtherText>
                </DayBlock>
              ))}
            <BodyText weight='600'>{workerRollup(wDays, rollupLabel)}</BodyText>
            <HonestTable
              columns={workerColumns(expandedId)}
              data={workerRows}
              keyField='id'
              onClick={(item) => toggleRow(item.id)}
            />
            {expandedWorker && (
              <DayBlock>
                <OtherText weight='600'>{expandedWorker.day.dateLabel}</OtherText>
                {expandedWorker.day.segments.length ? (
                  <>
                    <DayStrip
                      segments={expandedWorker.day.segments}
                      dateLabel={`${expandedWorker.day.dateLabel} · ${expandedWorker.day.loginAt} → ${expandedWorker.day.logoutAt}`}
                      variant='worker'
                      startHour={startHour}
                      windowHours={windowHours}
                    />
                    <OtherText grey>{workerMini(expandedWorker.day)}</OtherText>
                  </>
                ) : (
                  <OtherText grey>Strip not stored for this day — totals only.</OtherText>
                )}
              </DayBlock>
            )}
          </>
        )}
      </ContainerContent>
    </Container>
  )
}
